import {
  PreFlight,
  PreFlightDocument,
  PreFlightDocumentProgress,
  PreFlightDocumentStatus,
} from 'pupdocs/types';
import { Facet, FacetList, Nullable } from 'types';
import date from 'utilities/dates/date';

export const mapPreFlights = (preFlights?: Nullable<PreFlight[]>) => {
  return (
    preFlights?.map((preFlight) => ({
      ...preFlight,
      key: preFlight.id,
      breeder: {
        ...preFlight.breeder,
        breederName: `${preFlight.breeder.primaryUser.firstName} ${preFlight.breeder.primaryUser.lastName}`,
      },
      progress: calculateProgress(preFlight.documents),
      status: getPreFlightStatus(preFlight.documents),
      expiresOn:
        preFlight.documents.length > 0
          ? date.toUSLocale(getExpirationDate(preFlight.documents))
          : '--',
      lastDocumentEvent: {
        ...preFlight.lastDocumentEvent,
        readableName: preFlight.lastDocumentEvent
          ? `${preFlight.lastDocumentEvent.user.firstName} ${preFlight.lastDocumentEvent.user.lastName}`
          : '--',
        occurredAt: preFlight.lastDocumentEvent
          ? date.toUSLocale(preFlight.lastDocumentEvent.occurredAt)
          : '--',
      },
      travelDate: date.toUSLocale(getTravelDate(preFlight)),
    })) ?? []
  );
};

const calculateProgress = (documents: PreFlightDocument[]): PreFlightDocumentProgress => {
  if (documents.length === 0) {
    return {
      progressString: '0/0',
      progressQuantity: 0,
      totalDocuments: 0,
    };
  }
  const totalDocuments = documents.length;
  const completeDocuments = documents.filter(
    (doc) => doc.workflowEntity.currentStatusId === 25,
  ).length;
  return {
    progressString: `${completeDocuments}/${totalDocuments}`,
    progressQuantity: completeDocuments,
    totalDocuments: totalDocuments,
  };
};

const getPreFlightStatus = (documents: PreFlightDocument[]): PreFlightDocumentStatus => {
  if (documents.length === 0) return 'No Documents';
  const progress = calculateProgress(documents);
  const pendingReviewDocuments = documents.filter(
    (doc) => doc.workflowEntity.currentStatusId === 23,
  ).length;
  if (progress && progress.totalDocuments === progress.progressQuantity) {
    return 'Finalized';
  }

  if (
    progress &&
    progress.progressQuantity < progress.totalDocuments &&
    pendingReviewDocuments > 0
  ) {
    return 'In Review';
  }

  return 'Pending Review';
};

const getExpirationDate = (documents: PreFlightDocument[]): string => {
  if (documents.length === 0) return '--';
  const nonExpiredDocuments = documents
    .filter((doc) => doc.workflowEntity.currentStatusId !== 26)
    .slice()
    .sort((a, b) => a.expiresAt.localeCompare(b.expiresAt));
  return nonExpiredDocuments[0].expiresAt;
};

export const getPreFlightsFacets = (preFlights?: Nullable<PreFlight[]>): FacetList => {
  const hash = new Map();
  const facets: Record<string, Facet[]> = {
    breederId: [],
    breeder: [],
    progress: [],
  };

  if (!preFlights) {
    return facets;
  }

  preFlights.forEach((p: PreFlight) => {
    const breederIdKey = `breederId-${p.breeder.id}`;
    const breederKey = `breeder-${p.breeder.primaryUser.firstName}`;
    const progressKey = `progress-${p.progress?.progressString}`;

    if (!hash.has(breederIdKey)) {
      hash.set(breederIdKey, true);
      facets.breederId.push({
        text: `${p.breeder.id}` ?? '',
        value: `${p.breeder.id}` ?? '',
      });
    }

    if (!hash.has(breederKey)) {
      hash.set(breederKey, true);
      facets.breeder.push({
        text: p.breeder.primaryUser.firstName,
        value: p.breeder.primaryUser.firstName.toLowerCase(),
      });
    }

    if (!hash.has(progressKey)) {
      hash.set(progressKey, true);
      facets.progress.push({
        text: p.progress?.progressString ?? '',
        value: p.progress?.progressString ?? '',
      });
    }
  });
  return facets;
};

const getTravelDate = (record: PreFlight): string =>
  record.firstTravelSequence?.sequencable?.departureTime ||
  record.currentTravelProposal?.departureDate;
